import React, { useState, useEffect } from 'react';
import { X, Shield, AlertCircle } from 'lucide-react';


export default function EditRoleModal({ role, allRoles, onClose, onSave }) {
  const [formData, setFormData] = useState({
    displayName: '',
    description: '',
    hierarchyLevel: 50,
    overseesRoleIds: []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (role) {
      setFormData({
        displayName: role?.displayName || '',
        description: role?.description || '',
        hierarchyLevel: role?.hierarchyLevel || 50,
        overseesRoleIds: role?.overseesRoleIds || []
      });
    }
  }, [role]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');
    setLoading(true);

    try {
      await onSave(role?.id, formData);
      onClose();
    } catch (err) {
      setError(err?.message || 'Failed to update role');
    } finally {
      setLoading(false);
    }
  };

  const handleOversightChange = (roleId) => {
    setFormData(prev => ({
      ...prev,
      overseesRoleIds: prev?.overseesRoleIds?.includes(roleId)
        ? prev?.overseesRoleIds?.filter(id => id !== roleId)
        : [...prev?.overseesRoleIds, roleId]
    }));
  };

  // Hierarchy level options ordered from highest to lowest authority
  const hierarchyLevels = [
    { value: 5, label: '1 - Super Administrator', description: 'Highest authority, system-wide access' },
    { value: 15, label: '2 - Business Administrator', description: 'Tenant-level administration' },
    { value: 30, label: '3 - Manager', description: 'Team and department management' },
    { value: 50, label: '4 - Staff', description: 'Standard user access' },
    { value: 80, label: '5 - Customer', description: 'External customer access' }
  ];

  // Filter out the current role and roles with higher hierarchy (can't oversee higher roles)
  // A role can only oversee roles with LOWER authority (higher hierarchy level numbers)
  // CRITICAL: Super Admin (hierarchy <= 10) should NEVER appear in oversight for non-Super Admin roles
  const availableRoles = allRoles?.filter(r => {
    // Exclude current role
    if (r?.id === role?.id) return false;
    
    // Exclude Super Admin from oversight options for all non-Super Admin roles
    if (r?.hierarchyLevel <= 10 && formData?.hierarchyLevel > 10) return false;
    
    // Only show roles with lower authority (higher hierarchy numbers)
    return r?.hierarchyLevel > formData?.hierarchyLevel;
  });

  const getHierarchyBadge = (level) => {
    if (level <= 10) return { text: 'System Admin', color: 'bg-red-100 text-red-800' };
    if (level <= 20) return { text: 'Business Admin', color: 'bg-purple-100 text-purple-800' };
    if (level <= 40) return { text: 'Manager', color: 'bg-blue-100 text-blue-800' };
    if (level <= 60) return { text: 'Staff', color: 'bg-green-100 text-green-800' };
    return { text: 'Customer', color: 'bg-gray-100 text-gray-800' };
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <Shield className="w-6 h-6 text-indigo-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Edit Role</h2>
              <p className="text-sm text-gray-500">Update role details and oversight relationships</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          )}

          {/* Role Name (Read-only) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Role Name
            </label>
            <input
              type="text"
              value={role?.name || ''}
              disabled
              className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
            />
            <p className="text-xs text-gray-500 mt-1">Role name cannot be changed</p>
          </div>

          {/* Display Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Display Name *
            </label>
            <input
              type="text"
              value={formData?.displayName}
              onChange={(e) => setFormData({ ...formData, displayName: e?.target?.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              placeholder="Enter display name"
              required
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={formData?.description}
              onChange={(e) => setFormData({ ...formData, description: e?.target?.value })}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              placeholder="Enter role description"
            />
          </div>

          {/* Hierarchy Level */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Hierarchy Level *
            </label>
            <p className="text-xs text-gray-500 mb-3">
              Select the authority level for this role (ordered from highest to lowest)
            </p>
            <div className="space-y-2">
              {hierarchyLevels?.map((level) => {
                const isSelected = formData?.hierarchyLevel === level?.value;
                return (
                  <label
                    key={level?.value}
                    className={`flex items-start p-3 border-2 rounded-lg cursor-pointer transition-all ${
                      isSelected
                        ? 'border-indigo-500 bg-indigo-50' :'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                  >
                    <div className="flex items-center h-5">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => setFormData({ ...formData, hierarchyLevel: level?.value })}
                        className="w-5 h-5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                      />
                    </div>
                    <div className="ml-3 flex-1">
                      <div className="text-sm font-medium text-gray-900">
                        {level?.label}
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {level?.description}
                      </p>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Oversight Relationships */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Roles This Role Oversees
            </label>
            <p className="text-xs text-gray-500 mb-3">
              Select which roles this role has oversight authority over. You can only oversee roles at the same or lower hierarchy level.
            </p>
            <div className="border border-gray-200 rounded-lg divide-y divide-gray-200 max-h-64 overflow-y-auto">
              {availableRoles?.length === 0 ? (
                <div className="p-4 text-center text-gray-500 text-sm">
                  No roles available for oversight at this hierarchy level
                </div>
              ) : (
                availableRoles?.map((availableRole) => {
                  const badge = getHierarchyBadge(availableRole?.hierarchyLevel);
                  return (
                    <label
                      key={availableRole?.id}
                      className="flex items-center p-3 hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={formData?.overseesRoleIds?.includes(availableRole?.id)}
                        onChange={() => handleOversightChange(availableRole?.id)}
                        className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                      />
                      <div className="ml-3 flex-1">
                        <div className="flex items-center space-x-2">
                          <span className="text-sm font-medium text-gray-900">
                            {availableRole?.displayName}
                          </span>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${badge?.color}`}>
                            {badge?.text}
                          </span>
                        </div>
                        {availableRole?.description && (
                          <p className="text-xs text-gray-500 mt-0.5">
                            {availableRole?.description}
                          </p>
                        )}
                      </div>
                    </label>
                  );
                })
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}